import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, NavLink } from 'react-router-dom';
import Home from './pages/Home';
import Compare from './pages/Compare';
import Results from './pages/Results';
import Manage from './pages/Manage';
import { REACT_APP_API_URL } from './config';
import { getUserEmail, clearUserData, isAuthenticated } from './utils/userUtils';
import './App.css';

// Create a fetch wrapper function to include the token automatically
const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('accessToken');
  
  // Prepare headers with the token
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers || {},
  };
  
  // Add Authorization header if token exists
  if (token) {
    // Ensure token is properly formatted
    const formattedToken = token.trim();
    console.log(`Using token (first 10 chars): ${formattedToken.substring(0, 10)}...`);
    headers['Authorization'] = `Bearer ${formattedToken}`;
  } else {
    console.warn('No access token available for API request');
  }
  
  // Merge the headers with the options
  const requestOptions = {
    ...options,
    headers
  };
  
  // Ensure we have a properly formatted URL
  let fullUrl;
  if (url.startsWith('http')) {
    console.log("URL is already a full URL:", url);
    // Already a full URL
    fullUrl = url;
  } else {
    // Handle different URL path formats
    if (url.startsWith('/')) {
      // Remove leading slash for consistency
      fullUrl = `${REACT_APP_API_URL}${url}`;
    } else {
      fullUrl = `${REACT_APP_API_URL}/${url}`;
    }
  }
  
  // Log the request for debugging
  console.log('API Request: ' + fullUrl);
  
  try {
    // Create a unique timer label to prevent conflicts
    const timerLabel = 'Fetch-' + Date.now() + '-' + fullUrl.replace(/[^a-zA-Z0-9]/g, '-');
    console.time(timerLabel);
    
    const response = await fetch(fullUrl, requestOptions);
    
    console.timeEnd(timerLabel);
    console.log(`Response status for ${fullUrl}:`, response.status);
    
    // Handle authentication errors more gracefully
    if (response.status === 401) {
      console.error('Authentication error:', await response.text());
      
      // Don't redirect if this is an auth-related endpoint
      const isAuthEndpoint = fullUrl.includes('/auth/') || fullUrl.includes('/current-user');
      
      // Only clear token if this isn't an auth-related endpoint and we have a token
      if (!isAuthEndpoint && token) {
        console.warn('Clearing invalid token due to 401');
        localStorage.removeItem('accessToken');
        
        // Only redirect if we're not already on the home page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
          throw new Error('Authentication failed. Please log in again.');
        }
      }
    }
    
    return response;
  } catch (error) {
    console.error(`API request failed for ${fullUrl}:`, error);
    throw error;
  }
};

function App() {
  const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Clear auth errors when navigating to a different page
  useEffect(() => {
    if (authError) {
      console.log('Clearing auth error due to navigation');
      setAuthError(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      // Set loading state
      setIsLoading(true);
      
      try {
        // First check for stored token
        const storedToken = localStorage.getItem('accessToken');
        
        if (storedToken) {
          console.log("Found stored token, verifying...");
          setAccessToken(storedToken);
          
          try {
            // Use getUserEmail to get and store the email
            const email = await getUserEmail();
            setUserEmail(email);
            console.log("User authenticated, email:", email);
            setAuthError(null); // Clear any previous auth errors
            setIsLoading(false);
            return;
          } catch (error) {
            console.error("Error verifying stored token:", error);
            clearUserData();
            setAccessToken(null);
            setAuthError("Session expired. Please log in again.");
          }
        }
        
        // Then check for code in URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        
        if (code) {
          console.log("Found auth code:", code.substring(0, 10) + "...");
          
          // Check if we've processed this code already - prevents double processing
          const processedCode = sessionStorage.getItem('processedCode');
          if (processedCode === code) {
            console.log("Already processed this auth code, skipping");
            setIsLoading(false);
            return;
          }
          
          // Set the code as processed BEFORE making the request to prevent race conditions
          sessionStorage.setItem('processedCode', code);
          
          // Clear URL params to prevent reprocessing on refresh
          window.history.replaceState({}, document.title, window.location.pathname);
          
          // Call the backend callback with the code
          try {
            const response = await apiFetch('auth/callback?code=' + code);
            
            if (response.ok) {
              // Get the token from the response
              const newToken = await response.text();
              console.log("Received access token:", newToken.substring(0, 10) + "...");
              
              // Validate token
              if (!newToken || newToken.trim() === '') {
                console.error("Received empty or invalid token");
                setAuthError("Received invalid authentication token");
                setIsLoading(false);
                return;
              }
              
              // Clean the token (remove any whitespace or quotes)
              const cleanToken = newToken.trim().replace(/^["'](.*)["']$/, '$1');
              
              // Store the token
              localStorage.setItem('accessToken', cleanToken);
              setAccessToken(cleanToken);
              
              // Use getUserEmail to get and store the email
              try {
                const email = await getUserEmail();
                setUserEmail(email);
                console.log("User authenticated:", email);
                setAuthError(null); // Clear any previous auth errors
              } catch (error) {
                console.error("Failed to get user info:", error);
                setAuthError("Failed to get user information");
                clearUserData();
                setAccessToken(null);
              }
            } else {
              const errorText = await response.text();
              console.error("Failed to get token:", errorText);
              setAuthError("Failed to authenticate");
            }
          } catch (error) {
            console.error("Error calling backend callback:", error);
            setAuthError("Failed to authenticate");
          }
        }
      } finally {
        // Always finish loading
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleLogin = () => {
    // Use apiFetch for consistency with other auth endpoints
    console.log('Redirecting to login ' + REACT_APP_API_URL + '/auth/login');
    window.location.href = REACT_APP_API_URL + '/auth/login';
  };

  const handleLogout = async () => {
    try {
      await apiFetch('auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      clearUserData();
      setAccessToken(null);
      setUserEmail(null);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="App">
      <header className="app-header">
        <div className="app-title">
          <h1>Image Arena</h1>
        </div>
        <div className="app-auth">
          {isLoading ? (
            <p>Loading...</p>
          ) : userEmail ? (
            <>
              <span className="user-email">{userEmail}</span>
              <button onClick={handleLogout} className="logout-button">Logout</button>
            </>
          ) : (
            <button onClick={handleLogin} className="login-button">Login</button>
          )}
        </div>
      </header>

      {authError && (
        <div className="auth-error">
          Authentication Error: {authError}
        </div>
      )}

      <div className="app-layout">
        {userEmail && (
          <nav className="app-nav">
            <ul className="nav-list">
              <li className="nav-item">
                <NavLink to="/" className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>
                  Home
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/compare" className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>
                  Compare
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/results" className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>
                  Results
                </NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/manage" className={({isActive}) => isActive ? "nav-link active" : "nav-link"}>
                  Manage
                </NavLink>
              </li>
            </ul>
            
            <div className="nav-info">
              <h3>Get Started</h3>
              <ol>
                <li>Upload images for at least two models</li>
                <li>Go to Compare to start rating images</li>
                <li>Check your results in the Results page</li>
              </ol>
              
              <div className="user-box">
                <h3>User Info</h3>
                <p>{userEmail}</p>
              </div>
            </div>
          </nav>
        )}

        <div className={`app-content ${userEmail ? 'with-nav' : ''}`}>
          {isLoading ? (
            <div className="loading">Loading...</div>
          ) : userEmail ? (
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/compare" element={<Compare />} />
              <Route path="/compare/:experimentId" element={<Compare />} />
              <Route path="/results" element={<Results />} />
              <Route path="/results/:experimentId" element={<Results />} />
              <Route path="/manage" element={<Manage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          ) : (
            <Routes>
              <Route path="/" element={<Home onLogin={handleLogin} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          )}
        </div>
      </div>
    </div>
  );
}

// Export the apiFetch function for use in other components
export { apiFetch };
export default App;
